import { JSX, useState } from "react";

import { Cookie, Lock, Logout, MenuBook } from "@mui/icons-material";
import { Avatar, Box, Divider, ListItemAvatar, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { resetCookieConsentValue } from "react-cookie-consent";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useConsent } from "src/components/GoogleAnalytics/ConsentProvider";
import { API_URL } from "src/lib/config.helper";
import { signOut } from "src/lib/user.helper";

import { avatarStyles, getAvatarName } from "./helpers/Profile.helper";
import ProfileButton from "./ProfileButton";

const styles = {
  profileMenu: {
    "aria-labelledby": "profile-toggle-button",
  },
};

function Profile(): JSX.Element {
  const queryClient = useQueryClient();
  const { setConsent, setForceBanner } = useConsent();
  const auth = useAuth();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleLogout = (): void => {
    signOut(queryClient, auth);
    ReactGA.set({ userId: undefined });
  };

  return (
    <Box>
      <ProfileButton
        handleClick={handleClick}
        isOpen={isOpen}
        username={auth.user?.profile.given_name}
        familyName={auth.user?.profile.family_name}
      />
      <Menu id='profile-menu' anchorEl={anchorEl} open={isOpen} onClose={handleClose} MenuListProps={styles.profileMenu}>
        <MenuItem>
          <ListItemAvatar>
            <Avatar sx={avatarStyles}>
              {getAvatarName(auth.user?.profile.given_name)}
              {getAvatarName(auth.user?.profile.family_name)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary={auth.user?.profile.name} secondary={auth.user?.profile.preferred_username} />
        </MenuItem>
        <Divider />

        <MenuItem component='a' href={`${API_URL.replace(/\/api\/?$/, "")}/swagger/index.html`} target='_blank' rel='noopener'>
          <ListItemIcon>
            <MenuBook />
          </ListItemIcon>
          <ListItemText>{t("API Documentation")}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            resetCookieConsentValue("userConsent");
            setConsent(undefined);
            setForceBanner(true);
          }}
        >
          <ListItemIcon>
            <Cookie />
          </ListItemIcon>
          <ListItemText>{t("Change Cookies")}</ListItemText>
        </MenuItem>

        <MenuItem component='a' href='https://ase.ch/en/disclaimer/' target='_blank' rel='noopener'>
          <ListItemIcon>
            <Lock />
          </ListItemIcon>
          <ListItemText>{t("Privacy Policy")}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText>{t("signout")}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default Profile;
